import { ChangeEvent, MouseEvent, useCallback, Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { Theme } from 'shared/models';
import { TextField } from "ui/shared/controls";
import { deepSet } from 'logic/deepSet';

require('./CustomParametersField.css');

type ItemProps = {
    index: number;
    optionKey: string;
    optionValue: string;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
    onKeyChange(newKey: string): void;
    onClickDelete(e: MouseEvent<HTMLElement>): void;
};

const CustomParameterRow = ({ index, optionKey, optionValue, onChange, onClickDelete, onKeyChange }: ItemProps) => {
    const { t } = useTranslation();

    return (
        <>
            <TextField
                id={ `theme-option-${index}-key` }
                className="_optionKey"
                name={ optionKey }
                value={ optionKey || "" }
                onChange={ e => { onKeyChange(e.target.value); } }
            />
            <i className="grey equal icon">=</i>
            <TextField
                id={ `theme-option-${index}-value` }
                className="_optionValue"
                name={ `options[${optionKey}]` }
                value={ optionValue || "" }
                onChange={ onChange }
            />
            <button
                className="ui tiny basic icon button"
                title={ t('COMMAND.REMOVE') }
                data-index={ index }
                onClick={ onClickDelete }
            >
                <i className="ui red remove icon"/>
            </button>
        </>
    );
};

type NewItemProps = {
    onAdd(name: string, value: string): void;
};

const validKeyPattern = /^[a-zA-Z_][0-9a-zA-Z_]*$/;

const NewCustomParameterRow = ({ onAdd }: NewItemProps) => {
    const { t } = useTranslation();

    const [ newOptionKey, setNewOptionKey ] = useState('');
    const [ newOptionValue, setNewOptionValue ] = useState('');

    const handleNewOptionKeyChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const newKey = e.target.value;
        if (validKeyPattern.test(newKey) || newKey === '') {
            setNewOptionKey(newKey);
        }
    }, [ setNewOptionKey ]);

    const handleNewOptionValueChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setNewOptionValue(e.target.value);
    }, [ setNewOptionValue ]);

    const handleClickAdd = () => {
        if (newOptionKey && newOptionValue) {
            onAdd(newOptionKey, newOptionValue);
            setNewOptionKey('');
            setNewOptionValue('');
        }
    };

    return (
        <>
            <TextField
                id="new-option-key"
                name="optionKey"
                value={ newOptionKey }
                placeholder={ t('THEMES.EDIT_DIALOG.LABELS.NAME') }
                onChange={ handleNewOptionKeyChange }
            />
            <i className="grey equal icon">=</i>
            <TextField
                id="new-option-value"
                name="optionValue"
                value={ newOptionValue }
                placeholder={ t('THEMES.EDIT_DIALOG.LABELS.VALUE') }
                onChange={ handleNewOptionValueChange }
            />
            <button
                type="button"
                className="ui tiny icon button"
                title={ t('COMMAND.ADD') }
                onClick={ handleClickAdd }
            >
                <i className="ui add icon"/>
            </button>
        </>
    );
};

export type Props = {
    className?: string;
    theme: Partial<Theme>;
    setData: Dispatch<SetStateAction<Partial<Theme>>>;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
};

export const CustomParametersField = ({ className, theme, setData, onChange }: Props) => {
    const { t } = useTranslation();
     
    const handleClickDelete = useCallback((e: MouseEvent<HTMLElement>) => {
        const indexString = e.currentTarget.dataset['index']!;
        const index = parseInt(indexString, 10);

        setData(theme => ({
            ...theme,
            options: Object.fromEntries(Object.entries(theme.options || {}).filter((_, i) => i !== index)),
        }));
    }, [ setData ]);

    const handleAdd = useCallback((name: string, value: string) => {
        setData(theme => deepSet(theme, `options.${name}`, value));
    }, [ setData ]);

    const handleKeyChange = useCallback((newKey: string, oldKey: string) => {
        const entries = Object.entries(theme.options || {});

        if (newKey && validKeyPattern.test(newKey) && !Object.prototype.hasOwnProperty.call(theme.options, newKey)) {
            const newOptions: Theme['options'] = {};

            for (const [ key, value ] of entries) {
                if (key === oldKey) {
                    newOptions[newKey] = value;
                }
                else {
                    newOptions[key] = value;
                }
            }

            setData(theme => ({ ...theme, options: { ...newOptions } }));
        }
    }, [ setData, theme.options ]);

    return (
        <div
            id="custom-parameters-field"
            className={ `ui field column ${className || ''}` }
        >
            <label>
                { t('THEMES.EDIT_DIALOG.LABELS.CUSTOM_PARAMETERS') }
            </label>
            <div className="_container">
                <div className="_table">
                    <NewCustomParameterRow
                        onAdd={ handleAdd }
                    />
                    { Object.entries(theme.options || {}).map(([ key, value ], i) =>
                        <CustomParameterRow
                            key={ key }
                            index={ i }
                            optionKey={ key }
                            optionValue={ value }
                            onChange={ onChange }
                            onKeyChange={ newKey => handleKeyChange(newKey, key) }
                            onClickDelete={ handleClickDelete }
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
